import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, onSnapshot, runTransaction, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import cloudSyncService from '../services/cloudSyncService';
import firebaseAuthService from '../services/firebaseAuthService';
import accessControlService from '../services/accessControlService';
import deletedItemsService from '../services/deletedItemsService';
import universalDataService from '../services/universalDataService';
import { useTranslation } from '../services/translationService';
import { ConfirmPanel, Field, Icon, ICON, Panel, formatDate } from './ui';
import {
  ROLES, ROLE_LABELS, TYPE_LABELS, addCompany, addLocation, arendaToRestore, cadastralEntries, cadastralFile, cadastralRecordTotal, companiesText,
  countUnsynced, emptyUser, invoicesToRestore, migrateCadastral, normalizeSettings, parcelsToRestore, parseCadastralData, removeCompany, removeLocation,
  setCompanyDetails, splitTNumbers, toggled, userError,
} from './settingsLogic';
import './Settings.css';
import './Motorina.css';

const S_ICON = {
  check: 'M5 13l4 4L19 7',
  wait: 'M12 7v5l3 2M12 21a9 9 0 100-18 9 9 0 000 18z',
  next: 'M9 5l7 7-7 7',
  companies: 'M4 21V6l8-3v18M12 9l8 2v10M2 21h20',
  cadastral: 'M4 6c0-2 16-2 16 0v12c0 2-16 2-16 0zM4 6c0 2 16 2 16 0M4 12c0 2 16 2 16 0',
  users: 'M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c0-4 3-7 7-7s7 3 7 7M17 4a4 4 0 010 7M18 14c2 1 4 3 4 7',
  language: 'M12 3a9 9 0 100 18 9 9 0 000-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18',
  bin: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
};

const count = (value, one, many) => `${(value || 0).toLocaleString('ro-RO')} ${value === 1 ? one : many}`;

// The real services. The test page passes fake ones with the same names.
const realServices = (farmId, session) => ({
  farmId,
  role: session?.user?.role,
  uid: session?.user?.uid,
  farmName: session?.farm?.name,
  watch(onData, onError) {
    return onSnapshot(doc(db, 'farmData', farmId), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      const settings = data.companySettings?.data || data.companySettings || {};
      const cadastral = data.cadastralDatabase?.data || data.cadastralDatabase || {};
      onData(settings, cadastral);
      try {
        const now = new Date().toISOString();
        localStorage.setItem(`companySettings_${farmId}`, JSON.stringify({ data: settings, lastModified: now, synced: true, farmId, dataType: 'companySettings' }));
        localStorage.setItem(`cadastralDatabase_${farmId}`, JSON.stringify({ data: cadastral, lastModified: now, synced: true, farmId, dataType: 'cadastralDatabase' }));
      } catch { /* copy for offline use only */ }
    }, onError);
  },
  // Starts from the newest saved copy, so nothing another device saved gets overwritten.
  async saveField(dataType, change) {
    const ref = doc(db, 'farmData', farmId);
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const saved = snapshot.data()?.[dataType];
      const next = change(saved?.data || saved || {});
      const now = new Date().toISOString();
      transaction.set(ref, { [dataType]: { data: next, lastModified: now, dataType }, lastModified: now }, { mergeFields: [dataType, 'lastModified'] });
    });
  },
  watchBin: (callback) => deletedItemsService.subscribe(farmId, callback),
  async restore(entry) {
    const { _data, _originalType } = entry;
    const items = Array.isArray(_data) ? _data : [_data];
    const ref = doc(db, 'farmData', farmId);
    const now = new Date().toISOString();
    let restored = 0;

    if (_originalType === 'parcels') {
      const current = await universalDataService.loadParcels() || [];
      const fresh = parcelsToRestore(current, items);
      await universalDataService.saveParcels([...current, ...fresh]);
      window.dispatchEvent(new Event('parcelDataUpdated'));
      restored = fresh.length;
    } else if (_originalType === 'invoice') {
      const snapshot = await getDoc(ref);
      const saved = snapshot.exists() ? snapshot.data().invoices?.data : [];
      const current = Array.isArray(saved) ? saved : [];
      const fresh = invoicesToRestore(current, items);
      await setDoc(ref, { invoices: { data: [...current, ...fresh], lastModified: now, dataType: 'invoices' }, lastModified: now }, { mergeFields: ['invoices', 'lastModified'] });
      restored = fresh.length;
    } else if (_originalType === 'arenda') {
      const snapshot = await getDoc(ref);
      const current = snapshot.exists() ? snapshot.data().arendaData?.data || [] : [];
      const fresh = arendaToRestore(current, items);
      if (fresh.length === 0) return { kept: true };
      await setDoc(ref, { arendaData: { data: [...current, ...fresh], lastModified: now, dataType: 'arendaData' }, lastModified: now }, { mergeFields: ['arendaData', 'lastModified'] });
      window.dispatchEvent(new Event('arendaDataUpdated'));
      restored = fresh.length;
    }
    await deletedItemsService.permanentlyDelete(farmId, entry._deletedId);
    return { restored };
  },
  deleteForever: (id) => deletedItemsService.permanentlyDelete(farmId, id),
  syncNow: () => cloudSyncService.performCompleteSync(),
  downloadAgain: () => cloudSyncService.downloadAllDataOnLogin(farmId),
  verifyCloud: () => cloudSyncService.verifyFirebaseData(farmId),
  async loadUsers() {
    const list = await firebaseAuthService.getFarmUsers(session.farm.id);
    return Promise.all(list.map(async (user) => ({ ...user, permissions: await accessControlService.getUserPermissions(user.uid) })));
  },
  async createUser(form) {
    const defaults = accessControlService.getDefaultPermissions(form.role);
    const permissions = {
      companies: form.companies.length > 0 ? form.companies : defaults.companies,
      pages: form.pages.length > 0 ? form.pages : defaults.pages,
      actions: defaults.actions,
    };
    const created = await firebaseAuthService.createUserAccount({ name: form.name.trim(), email: form.email.trim(), role: form.role }, session.user.uid);
    if (permissions.companies.length > 0 || permissions.pages.length > 0) await accessControlService.updateUserPermissions(created.uid, permissions);
    return created;
  },
  updatePermissions: (uid, permissions) => accessControlService.updateUserPermissions(uid, permissions),
  resetPassword: (email) => firebaseAuthService.resetPassword(email),
  deleteUser: (uid) => firebaseAuthService.deleteUserAccount(uid),
  pages: () => accessControlService.getAllPages(),
  unsynced: () => countUnsynced(localStorage, farmId),
});

// ---------- Companies and locations ----------

const CompanyPanel = ({ name, settings, saving, onSave, onDefault, onDelete, onClose }) => {
  const saved = settings.companyDetails[name] || {};
  const [form, setForm] = useState({ administratorName: saved.administratorName || '', cui: saved.cui || '', jNumber: saved.jNumber || '', address: saved.address || '' });
  const isDefault = settings.defaultCompany === name;
  return (
    <Panel title={name} subtitle={isDefault ? 'Firma implicită' : 'Detalii firmă'} onClose={onClose}
      footer={<button type="button" className="pm-btn-primary" disabled={saving} onClick={() => onSave(form)}>{saving ? 'Se salvează…' : 'Salvează detaliile'}</button>}>
      <p className="pm-muted">Aceste date apar pe contractele de arendă ale firmei.</p>
      <Field label="Nume administrator"><input placeholder="ex: Ion Popescu" value={form.administratorName} onChange={(e) => setForm({ ...form, administratorName: e.target.value })} /></Field>
      <div className="pm-two">
        <Field label="CUI"><input value={form.cui} onChange={(e) => setForm({ ...form, cui: e.target.value })} /></Field>
        <Field label="Număr J"><input placeholder="J22/1234/2020" value={form.jNumber} onChange={(e) => setForm({ ...form, jNumber: e.target.value })} /></Field>
      </div>
      <Field label="Adresa sediului"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
      <div className="se-item-actions">
        {!isDefault && <button type="button" className="pm-btn-soft" onClick={onDefault}>Fă-o implicită</button>}
        <button type="button" className="pm-btn-soft pm-danger" disabled={settings.companies.length <= 1} onClick={onDelete}>Șterge firma</button>
      </div>
      {settings.companies.length <= 1 && <p className="pm-muted">Ultima firmă nu se poate șterge.</p>}
    </Panel>
  );
};

const CompaniesPanel = ({ settings, onOpenCompany, onAddCompany, onAddLocation, onDefaultLocation, onDeleteLocation, onClose }) => {
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const submit = (event, value, add, clear) => {
    event.preventDefault();
    if (add(value.trim())) clear('');
  };
  return (
    <Panel title="Companii și locații" subtitle={`${count(settings.companies.length, 'firmă', 'firme')}, ${count(settings.locations.length, 'locație', 'locații')}`} onClose={onClose}>
      <h3>Firme</h3>
      <div className="se-list">
        {settings.companies.length === 0 && <p className="pm-muted">Nu sunt firme încă.</p>}
        {settings.companies.map((name) => (
          <button key={name} type="button" className="pm-row" onClick={() => onOpenCompany(name)}>
            <div className="pm-row-main">
              <strong>{name}</strong>
              <span>{settings.companyDetails[name]?.administratorName ? `Administrator ${settings.companyDetails[name].administratorName}` : 'Fără detalii încă'}</span>
            </div>
            {settings.defaultCompany === name && <span className="se-badge">Implicită</span>}
            <span className="se-chevron"><Icon d={S_ICON.next} size={16} /></span>
          </button>
        ))}
      </div>
      <form className="se-add" onSubmit={(e) => submit(e, company, onAddCompany, setCompany)}>
        <input aria-label="Firmă nouă" placeholder="Firmă nouă" value={company} onChange={(e) => setCompany(e.target.value)} />
        <button type="submit" className="pm-btn-primary" disabled={!company.trim()}>Adaugă</button>
      </form>

      <h3>Locații</h3>
      <div className="se-list">
        {settings.locations.length === 0 && <p className="pm-muted">Nu sunt locații încă.</p>}
        {settings.locations.map((name) => (
          <div key={name} className="se-item">
            <div className="pm-card-head">
              <div className="se-item-main"><strong>{name}</strong></div>
              {settings.defaultLocation === name && <span className="se-badge">Implicită</span>}
            </div>
            <div className="se-item-actions">
              {settings.defaultLocation !== name && <button type="button" className="pm-btn-soft" onClick={() => onDefaultLocation(name)}>Fă-o implicită</button>}
              <button type="button" className="pm-btn-soft pm-danger" aria-label={`Șterge ${name}`} disabled={settings.locations.length <= 1} onClick={() => onDeleteLocation(name)}>Șterge</button>
            </div>
          </div>
        ))}
      </div>
      <form className="se-add" onSubmit={(e) => submit(e, location, onAddLocation, setLocation)}>
        <input aria-label="Locație nouă" placeholder="Locație nouă" value={location} onChange={(e) => setLocation(e.target.value)} />
        <button type="submit" className="pm-btn-primary" disabled={!location.trim()}>Adaugă</button>
      </form>
    </Panel>
  );
};

// ---------- Cadastral data ----------

const CadastralPanel = ({ files, saving, onAdd, onDelete, onClose }) => {
  const [comuna, setComuna] = useState('');
  const [tText, setTText] = useState('');
  const [pasted, setPasted] = useState('');
  const [tried, setTried] = useState(false);
  const entries = cadastralEntries(files);
  const ready = comuna.trim() && splitTNumbers(tText).length > 0;

  const addText = async (text, fileName) => {
    setTried(true);
    if (!ready || !text.trim()) return;
    const done = await onAdd({ text, comuna: comuna.trim(), tNumbers: splitTNumbers(tText), fileName });
    if (done) { setComuna(''); setTText(''); setPasted(''); setTried(false); }
  };
  const chooseFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await addText(await file.text(), file.name);
    event.target.value = '';
  };

  return (
    <Panel title="Baza de date cadastrală" subtitle={count(cadastralRecordTotal(files), 'înregistrare', 'înregistrări')} onClose={onClose}>
      <p className="pm-muted">Cu aceste date, Manager Parcele completează singur numărul cadastral când adaugi o parcelă.</p>
      <div className="pm-card">
        <h3>Adaugă date</h3>
        <div className="pm-two">
          <Field label="Comuna" error={tried && !comuna.trim()}><input placeholder="ex: Mircești" value={comuna} onChange={(e) => setComuna(e.target.value)} /></Field>
          <Field label="Numerele T" error={tried && splitTNumbers(tText).length === 0}><input placeholder="ex: T36, T37" value={tText} onChange={(e) => setTText(e.target.value)} /></Field>
        </div>
        <Field label="Lipește din Excel, cu antetul" hint="Suprafața este în mp și devine hectare singură: 1600 mp = 0.16 ha.">
          <textarea className="se-paste" rows="6" aria-label="Date din Excel" value={pasted} onChange={(e) => setPasted(e.target.value)} />
        </Field>
        <button type="button" className="pm-btn-primary" disabled={saving || !pasted.trim()} onClick={() => addText(pasted, null)}>{saving ? 'Se salvează…' : 'Salvează datele lipite'}</button>
        <Field label="Sau alege un fișier .txt / .csv">
          <input type="file" className="se-file" accept=".txt,.csv" disabled={saving} aria-label="Fișier cadastral" onChange={chooseFile} />
        </Field>
      </div>
      <h3>Date existente</h3>
      <div className="se-list">
        {entries.length === 0 && <p className="pm-muted">Nu sunt date cadastrale încă.</p>}
        {entries.map((entry) => (
          <div key={entry.name} className="se-item">
            <div className="se-item-main">
              <strong>{entry.comuna || 'Fără comună'}: {(entry.tNumbers || []).join(', ')}</strong>
              <span>{count(entry.recordCount, 'înregistrare', 'înregistrări')} · {formatDate(entry.uploadDate)}</span>
              <span>{entry.name}</span>
            </div>
            <div className="se-item-actions">
              <button type="button" className="pm-btn-soft pm-danger" aria-label={`Șterge ${entry.name}`} onClick={() => onDelete(entry)}>Șterge</button>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

// ---------- Recycle bin ----------

const BinPanel = ({ items, busyId, onRestore, onDelete, onClose }) => (
  <Panel title="Coș de gunoi" subtitle={count(items.length, 'element', 'elemente')} onClose={onClose}>
    <p className="pm-muted">Ce ai șters rămâne aici până îl restaurezi sau îl ștergi definitiv.</p>
    <div className="se-list">
      {items.length === 0 && <p className="pm-empty">Coșul de gunoi este gol.</p>}
      {items.map((item) => (
        <div key={item._deletedId} className="se-item">
          <div className="se-item-main">
            <strong>{item._label}</strong>
            <span>{TYPE_LABELS[item._originalType] || item._originalType}</span>
            <span>Șters la {new Date(item._deletedAt).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })}{item._deletedBy ? ` de ${item._deletedBy}` : ''}</span>
          </div>
          <div className="se-item-actions">
            <button type="button" className="pm-btn-primary" disabled={busyId === item._deletedId} onClick={() => onRestore(item)}>
              {busyId === item._deletedId ? 'Se restaurează…' : 'Restaurează'}
            </button>
            <button type="button" className="pm-btn-soft pm-danger" disabled={busyId === item._deletedId} onClick={() => onDelete(item)}>Șterge definitiv</button>
          </div>
        </div>
      ))}
    </div>
  </Panel>
);

// ---------- People ----------

const ChipChoice = ({ label, options, selected, onToggle }) => (
  <div>
    <p className="mo-label">{label}</p>
    <div className="st-crops" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.id} type="button" className={selected.includes(option.id) ? 'is-on' : ''} aria-pressed={selected.includes(option.id)} onClick={() => onToggle(option.id)}>{option.name}</button>
      ))}
    </div>
  </div>
);

const NewUserPanel = ({ companies, pages, existingEmails, saving, onCreate, onClose }) => {
  const [form, setForm] = useState(emptyUser);
  const [error, setError] = useState('');
  const submit = (event) => {
    event.preventDefault();
    const problem = userError(form, existingEmails);
    setError(problem);
    if (!problem) onCreate(form);
  };
  return (
    <Panel title="Utilizator nou" onClose={onClose} onSubmit={submit}
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="submit" className="pm-btn-primary" disabled={saving}>{saving ? 'Se creează…' : 'Creează contul'}</button>
        </>
      )}>
      {error && <p className="pm-error">{error}</p>}
      <Field label="Nume complet"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Email"><input type="email" autoCapitalize="none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
      <div className="pm-segments" role="group" aria-label="Rol">
        {ROLES.map((role) => <button key={role} type="button" className={form.role === role ? 'is-on' : ''} aria-pressed={form.role === role} onClick={() => setForm({ ...form, role })}>{ROLE_LABELS[role]}</button>)}
      </div>
      <ChipChoice label="Firme" options={companies.map((name) => ({ id: name, name }))} selected={form.companies} onToggle={(id) => setForm({ ...form, companies: toggled(form.companies, id) })} />
      <ChipChoice label="Pagini" options={pages} selected={form.pages} onToggle={(id) => setForm({ ...form, pages: toggled(form.pages, id) })} />
      <p className="pm-note">Dacă nu alegi nimic, contul primește firmele și paginile obișnuite pentru rolul lui. După creare vezi o parolă temporară.</p>
    </Panel>
  );
};

const UserPanel = ({ user, companies, pages, saving, onSave, onReset, onDelete, onClose }) => {
  const [permissions, setPermissions] = useState(() => ({ ...user.permissions, companies: user.permissions?.companies || [], pages: user.permissions?.pages || [] }));
  const all = permissions.companies.includes('all');
  const isManager = user.role === 'manager';
  return (
    <Panel title={user.name} subtitle={user.email} onClose={onClose}
      footer={!isManager && <button type="button" className="pm-btn-primary" disabled={saving} onClick={() => onSave(permissions)}>{saving ? 'Se salvează…' : 'Salvează permisiunile'}</button>}>
      <div className="pm-card">
        <dl className="pm-rows">
          <div><dt>Rol</dt><dd>{ROLE_LABELS[user.role] || user.role}</dd></div>
          <div><dt>Status</dt><dd className={user.isActive ? '' : 'pm-warn'}>{user.isActive ? 'Activ' : 'Inactiv'}</dd></div>
        </dl>
      </div>
      {isManager ? <p className="pm-note">Un manager are acces la tot și nu poate fi schimbat de aici.</p> : (
        <>
          <label className="st-switch">
            <input type="checkbox" checked={all} onChange={(e) => setPermissions({ ...permissions, companies: e.target.checked ? ['all'] : [] })} />
            <span>Toate firmele</span>
          </label>
          {!all && <ChipChoice label="Firme" options={companies.map((name) => ({ id: name, name }))} selected={permissions.companies} onToggle={(id) => setPermissions({ ...permissions, companies: toggled(permissions.companies, id) })} />}
          <ChipChoice label="Pagini" options={pages} selected={permissions.pages} onToggle={(id) => setPermissions({ ...permissions, pages: toggled(permissions.pages, id) })} />
          <div className="se-item-actions">
            <button type="button" className="pm-btn-soft" onClick={onReset}>Resetează parola</button>
            <button type="button" className="pm-btn-soft pm-danger" onClick={onDelete}>Șterge contul</button>
          </div>
        </>
      )}
    </Panel>
  );
};

const UsersPanel = ({ users, farmName, onOpen, onNew, onClose }) => (
  <Panel title="Utilizatori" subtitle={farmName} onClose={onClose}
    footer={<button type="button" className="pm-btn-accent" onClick={onNew}><Icon d={ICON.plus} size={18} />Utilizator nou</button>}>
    <div className="se-list">
      {users.length === 0 && <p className="pm-empty">Nu sunt utilizatori încă.</p>}
      {users.map((user) => (
        <button key={user.uid} type="button" className="pm-row" onClick={() => onOpen(user)}>
          <div className="pm-row-main">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
            <span>{ROLE_LABELS[user.role] || user.role} · {companiesText(user)}</span>
            {!user.isActive && <span className="pm-warn">Inactiv</span>}
          </div>
          <span className="se-chevron"><Icon d={S_ICON.next} size={16} /></span>
        </button>
      ))}
    </div>
    <details className="pm-more">
      <summary>Ce poate face fiecare rol</summary>
      <dl className="pm-rows">
        <div><dt>Muncitor</dt><dd>Câmpuri, sarcini, inventar, interfața de lucru</dd></div>
        <div><dt>Contabil</dt><dd>Finanțe, parcele, arendă, rapoarte</dd></div>
        <div><dt>Manager</dt><dd>Acces la tot, inclusiv la utilizatori</dd></div>
      </dl>
    </details>
  </Panel>
);

const PasswordPanel = ({ user, onCopy, onClose }) => (
  <Panel title="Cont creat" subtitle={user.name} onClose={onClose}
    footer={<button type="button" className="pm-btn-primary" onClick={onClose}>Am notat parola</button>}>
    <div className="se-password">
      Parola temporară pentru {user.email}:
      <code>{user.temporaryPassword}</code>
      Dă-i-o persoanei într-un mod sigur. După ce închizi, nu o mai poți vedea aici.
    </div>
    <button type="button" className="pm-btn-soft" onClick={onCopy}>Copiază parola</button>
  </Panel>
);

// ---------- Page ----------

const Settings = ({ testServices }) => {
  const { language, setLanguage, availableLanguages } = useTranslation();
  const services = useMemo(() => testServices || realServices(localStorage.getItem('currentFarmId'), firebaseAuthService.getCurrentSession()), [testServices]);
  const isManager = services.role === 'manager';

  const [settings, setSettings] = useState(() => normalizeSettings(null));
  const [cadastral, setCadastral] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [binItems, setBinItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [unsynced, setUnsynced] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);

  const [panel, setPanel] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [userPanel, setUserPanel] = useState(null);
  const [newUser, setNewUser] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyBinId, setBusyBinId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const migrationDone = useRef(false);

  const showToast = (message, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => services.watch((nextSettings, nextCadastral) => {
    setSettings(normalizeSettings(nextSettings));
    setCadastral(nextCadastral);
    setLoaded(true);
  }, (error) => {
    console.error('Setări: live data failed', error);
    setLoaded(true);
  }), [services]);
  useEffect(() => services.watchBin(setBinItems), [services]);

  const loadUsers = async () => {
    try { setUsers(await services.loadUsers()); } catch (error) {
      console.error('Setări: users failed', error);
      showToast('Utilizatorii nu s-au încărcat.', 'error');
    }
  };
  useEffect(() => { if (isManager) loadUsers(); }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  // The sync card tells the truth: online or not, and how much still waits on this device.
  useEffect(() => {
    const refresh = () => { setUnsynced(services.unsynced()); setOnline(navigator.onLine); };
    const synced = () => { refresh(); setLastSync(new Date()); };
    refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    window.addEventListener('cloudSyncComplete', synced);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
      window.removeEventListener('cloudSyncComplete', synced);
    };
  }, [services]);

  const saveSettings = async (change, message) => {
    setSaving(true);
    try {
      await services.saveField('companySettings', (current) => change(normalizeSettings(current)));
      if (message) showToast(message);
      return true;
    } catch (error) {
      console.error('Setări: save failed', error);
      showToast('Nu s-a salvat. Verifică internetul și încearcă din nou.', 'error');
      return false;
    } finally { setSaving(false); }
  };

  const saveCadastral = async (change, message) => {
    setSaving(true);
    try {
      await services.saveField('cadastralDatabase', change);
      if (message) showToast(message);
      return true;
    } catch (error) {
      console.error('Setări: cadastral save failed', error);
      const full = /size|exceed/i.test(String(error?.message));
      showToast(full ? 'Spațiul fermei în cloud este plin. Șterge date cadastrale vechi și încearcă din nou.' : 'Nu s-a salvat. Verifică internetul și încearcă din nou.', 'error');
      return false;
    } finally { setSaving(false); }
  };

  // Old cadastral entries were saved without a comună; they all belong to Mircești.
  useEffect(() => {
    if (migrationDone.current || !loaded || !migrateCadastral(cadastral)) return;
    migrationDone.current = true;
    saveCadastral((current) => migrateCadastral(current) || current).then((ok) => { if (!ok) migrationDone.current = false; });
  }, [cadastral, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const tryAddName = (list, name, label, add) => {
    if (list.includes(name)) {
      showToast(`${label} există deja.`, 'error');
      return false;
    }
    saveSettings((current) => add(current, name), `${name} a fost adăugată.`);
    return true;
  };

  const askDeleteCompany = (name) => setConfirm({
    title: `Ștergi ${name}?`,
    text: 'Firma dispare din liste. Parcelele și facturile ei rămân salvate cu acest nume.',
    action: 'Șterge firma',
    onYes: async () => {
      setConfirm(null);
      if (await saveSettings((current) => removeCompany(current, name), `${name} a fost ștearsă.`)) setCompanyName(null);
    },
  });

  const askDeleteLocation = (name) => setConfirm({
    title: `Ștergi ${name}?`,
    text: 'Locația dispare din liste. Parcelele ei rămân salvate cu acest nume.',
    action: 'Șterge locația',
    onYes: () => { setConfirm(null); saveSettings((current) => removeLocation(current, name), `${name} a fost ștearsă.`); },
  });

  const addCadastral = async ({ text, comuna, tNumbers, fileName }) => {
    const owners = parseCadastralData(text, tNumbers);
    const count_ = Object.keys(owners).length;
    if (count_ === 0) {
      showToast('Nu am găsit nicio înregistrare. Datele trebuie copiate din Excel, cu antetul, separate prin Tab.', 'error');
      return false;
    }
    const name = fileName || `Paste_${comuna}_${tNumbers.join('_')}_${Date.now()}`;
    return saveCadastral((current) => ({ ...current, [name]: cadastralFile(owners, tNumbers, comuna) }),
      `${count(count_, 'înregistrare adăugată', 'înregistrări adăugate')} pentru ${comuna}, ${tNumbers.join(', ')}.`);
  };

  const askDeleteCadastral = (entry) => setConfirm({
    title: 'Ștergi aceste date?',
    text: `${entry.comuna || ''} ${(entry.tNumbers || []).join(', ')}, ${count(entry.recordCount, 'înregistrare', 'înregistrări')}. Manager Parcele nu le va mai folosi la completare. Ștergerea nu se poate anula.`,
    action: 'Șterge datele',
    onYes: () => {
      setConfirm(null);
      saveCadastral((current) => {
        const next = { ...current };
        delete next[entry.name];
        return next;
      }, 'Datele au fost șterse.');
    },
  });

  const restoreBinItem = async (item) => {
    setBusyBinId(item._deletedId);
    try {
      const result = await services.restore(item);
      if (result.kept) showToast('Persoana are deja o distribuție pe acel an. Elementul rămâne în coș; șterge întâi distribuția nouă dacă vrei să o restaurezi pe aceasta.', 'error');
      else showToast('Datele au fost restaurate.');
    } catch (error) {
      console.error('Setări: restore failed', error);
      showToast('Nu s-a restaurat. Elementul rămâne în coș. Încearcă din nou.', 'error');
    } finally { setBusyBinId(null); }
  };

  const askDeleteBinItem = (item) => setConfirm({
    title: 'Ștergi definitiv?',
    text: `${item._label}. Această ștergere nu se mai poate anula.`,
    action: 'Șterge definitiv',
    onYes: async () => {
      setConfirm(null);
      try {
        await services.deleteForever(item._deletedId);
        showToast('Elementul a fost șters definitiv.');
      } catch (error) {
        console.error('Setări: delete failed', error);
        showToast('Nu s-a șters. Încearcă din nou.', 'error');
      }
    },
  });

  const syncNow = async () => {
    setSyncing(true);
    try {
      const result = await services.syncNow();
      if (!result) throw new Error('no result');
      setLastSync(new Date());
      setUnsynced(services.unsynced());
      showToast('Sincronizarea s-a terminat.');
    } catch (error) {
      console.error('Setări: sync failed', error);
      showToast('Sincronizarea nu a reușit. Verifică internetul.', 'error');
    } finally { setSyncing(false); }
  };

  const askDownload = () => setConfirm({
    title: 'Descarci din nou din cloud?',
    text: 'Datele de pe acest dispozitiv se înlocuiesc cu cele din cloud. Modificările care nu au ajuns încă în cloud se pot pierde.',
    action: 'Descarcă',
    onYes: async () => {
      setConfirm(null);
      setSyncing(true);
      try {
        if (!await services.downloadAgain()) throw new Error('no result');
        setLastSync(new Date());
        showToast('Datele au fost descărcate din cloud.');
      } catch (error) {
        console.error('Setări: download failed', error);
        showToast('Descărcarea nu a reușit. Verifică internetul.', 'error');
      } finally { setSyncing(false); }
    },
  });

  const verifyCloud = async () => {
    setSyncing(true);
    try {
      showToast(await services.verifyCloud() ? 'Datele fermei sunt salvate în cloud.' : 'Nu am găsit date în cloud pentru această fermă.', 'info');
    } catch (error) {
      console.error('Setări: verify failed', error);
      showToast('Verificarea nu a reușit. Verifică internetul.', 'error');
    } finally { setSyncing(false); }
  };

  const createUser = async (form) => {
    setSaving(true);
    try {
      const created = await services.createUser(form);
      setNewUser(false);
      setCreatedUser(created);
      loadUsers();
    } catch (error) {
      console.error('Setări: create user failed', error);
      showToast(`Contul nu s-a creat: ${error.message}`, 'error');
    } finally { setSaving(false); }
  };

  const savePermissions = async (permissions) => {
    setSaving(true);
    try {
      await services.updatePermissions(userPanel.uid, permissions);
      setUserPanel(null);
      loadUsers();
      showToast('Permisiunile au fost salvate.');
    } catch (error) {
      console.error('Setări: permissions failed', error);
      showToast('Permisiunile nu s-au salvat. Încearcă din nou.', 'error');
    } finally { setSaving(false); }
  };

  const askResetPassword = (user) => setConfirm({
    title: 'Resetezi parola?',
    text: `${user.name} primește un email la ${user.email} cu un link pentru o parolă nouă.`,
    action: 'Trimite emailul',
    onYes: async () => {
      setConfirm(null);
      try {
        await services.resetPassword(user.email);
        showToast('Emailul a fost trimis.');
      } catch (error) {
        console.error('Setări: reset failed', error);
        showToast('Emailul nu s-a trimis. Încearcă din nou.', 'error');
      }
    },
  });

  const askDeleteUser = (user) => setConfirm({
    title: `Ștergi contul lui ${user.name}?`,
    text: 'Persoana nu se va mai putea conecta. Ștergerea nu se poate anula.',
    action: 'Șterge contul',
    onYes: async () => {
      setConfirm(null);
      try {
        await services.deleteUser(user.uid);
        setUserPanel(null);
        loadUsers();
        showToast('Contul a fost șters.');
      } catch (error) {
        console.error('Setări: delete user failed', error);
        showToast('Contul nu s-a șters. Încearcă din nou.', 'error');
      }
    },
  });

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(createdUser.temporaryPassword);
      showToast('Parola a fost copiată.');
    } catch { showToast('Nu am putut copia. Selectează parola și copiaz-o manual.', 'error'); }
  };

  const languageName = availableLanguages.find((item) => item.code === language)?.name || language;
  const rows = [
    { id: 'companies', icon: S_ICON.companies, label: 'Companii și locații', sub: `${count(settings.companies.length, 'firmă', 'firme')}, ${count(settings.locations.length, 'locație', 'locații')}` },
    { id: 'cadastral', icon: S_ICON.cadastral, label: 'Baza de date cadastrală', sub: count(cadastralRecordTotal(cadastral), 'înregistrare', 'înregistrări') },
    ...(isManager ? [{ id: 'users', icon: S_ICON.users, label: 'Utilizatori', sub: count(users.length, 'cont', 'conturi') }] : []),
    { id: 'language', icon: S_ICON.language, label: 'Limbă', sub: languageName },
    { id: 'bin', icon: S_ICON.bin, label: 'Coș de gunoi', sub: count(binItems.length, 'element', 'elemente') },
  ];

  let statusTitle = 'Totul este sincronizat';
  if (!online) statusTitle = 'Fără internet';
  else if (unsynced > 0) statusTitle = `${count(unsynced, 'pagină așteaptă', 'pagini așteaptă')} să se sincronizeze`;
  const statusOk = online && unsynced === 0;
  let statusText = 'Datele se sincronizează singure pe toate dispozitivele';
  if (!online) statusText = 'Lucrezi pe acest dispozitiv. Se sincronizează când revine internetul';
  else if (lastSync) statusText = `Ultima dată la ${lastSync.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className="pm se">
      <header className="pm-head"><h1>Setări</h1></header>

      <section className="se-hero" aria-label="Sincronizare">
        <div className={`se-status ${statusOk ? '' : 'is-wait'}`}>
          <Icon d={statusOk ? S_ICON.check : S_ICON.wait} size={22} />
          <div><strong>{statusTitle}</strong><span>{statusText}</span></div>
        </div>
        <div className="se-hero-actions">
          <button type="button" className="pm-btn-accent" disabled={syncing || !online} onClick={syncNow}>{syncing ? 'Se lucrează…' : 'Sincronizează'}</button>
          <button type="button" className="pm-btn-plain" onClick={() => setPanel('advanced')}>Opțiuni avansate</button>
        </div>
      </section>

      <nav className="se-menu" aria-label="Setări">
        {rows.map((row) => (
          <button key={row.id} type="button" className="se-row" onClick={() => setPanel(row.id)}>
            <span className="se-icon"><Icon d={row.icon} size={19} /></span>
            <span className="se-row-main"><strong>{row.label}</strong><span>{row.sub}</span></span>
            <span className="se-chevron"><Icon d={S_ICON.next} size={16} /></span>
          </button>
        ))}
      </nav>

      {panel === 'advanced' && (
        <Panel title="Opțiuni avansate" subtitle="Sincronizarea merge singură. Le folosești doar dacă ceva pare greșit." onClose={() => setPanel(null)}>
          <div className="se-item">
            <div className="se-item-main"><strong>Verifică datele din cloud</strong><span>Spune dacă datele fermei sunt salvate.</span></div>
            <div className="se-item-actions"><button type="button" className="pm-btn-soft" disabled={syncing || !online} onClick={verifyCloud}>Verifică</button></div>
          </div>
          <div className="se-item">
            <div className="se-item-main"><strong>Descarcă din nou din cloud</strong><span>Înlocuiește ce este pe acest dispozitiv cu ce este în cloud.</span></div>
            <div className="se-item-actions"><button type="button" className="pm-btn-soft" disabled={syncing || !online} onClick={askDownload}>Descarcă</button></div>
          </div>
        </Panel>
      )}

      {panel === 'companies' && (
        <CompaniesPanel settings={settings} onClose={() => setPanel(null)} onOpenCompany={setCompanyName}
          onAddCompany={(name) => tryAddName(settings.companies, name, 'Firma', addCompany)}
          onAddLocation={(name) => tryAddName(settings.locations, name, 'Locația', addLocation)}
          onDefaultLocation={(name) => saveSettings((current) => ({ ...current, defaultLocation: name }), `${name} este acum implicită.`)}
          onDeleteLocation={askDeleteLocation} />
      )}
      {companyName && (
        <CompanyPanel name={companyName} settings={settings} saving={saving} onClose={() => setCompanyName(null)}
          onSave={async (details) => { if (await saveSettings((current) => setCompanyDetails(current, companyName, details), 'Detaliile au fost salvate.')) setCompanyName(null); }}
          onDefault={() => saveSettings((current) => ({ ...current, defaultCompany: companyName }), `${companyName} este acum implicită.`)}
          onDelete={() => askDeleteCompany(companyName)} />
      )}

      {panel === 'cadastral' && <CadastralPanel files={cadastral} saving={saving} onAdd={addCadastral} onDelete={askDeleteCadastral} onClose={() => setPanel(null)} />}
      {panel === 'bin' && <BinPanel items={binItems} busyId={busyBinId} onRestore={restoreBinItem} onDelete={askDeleteBinItem} onClose={() => setPanel(null)} />}

      {panel === 'language' && (
        <Panel title="Limbă" subtitle="Se aplică imediat" onClose={() => setPanel(null)}>
          <div className="se-lang" role="group" aria-label="Limbă">
            {availableLanguages.map((item) => (
              <button key={item.code} type="button" className={language === item.code ? 'is-on' : ''} aria-pressed={language === item.code} onClick={() => setLanguage(item.code)}>{item.name}</button>
            ))}
          </div>
        </Panel>
      )}

      {panel === 'users' && isManager && (
        <UsersPanel users={users} farmName={services.farmName} onOpen={setUserPanel} onNew={() => setNewUser(true)} onClose={() => setPanel(null)} />
      )}
      {userPanel && (
        <UserPanel key={userPanel.uid} user={userPanel} companies={settings.companies} pages={services.pages()} saving={saving} onSave={savePermissions}
          onReset={() => askResetPassword(userPanel)} onDelete={() => askDeleteUser(userPanel)} onClose={() => setUserPanel(null)} />
      )}
      {newUser && (
        <NewUserPanel companies={settings.companies} pages={services.pages()} existingEmails={users.map((user) => user.email)} saving={saving} onCreate={createUser} onClose={() => setNewUser(false)} />
      )}
      {createdUser && <PasswordPanel user={createdUser} onCopy={copyPassword} onClose={() => setCreatedUser(null)} />}

      {confirm && <ConfirmPanel {...confirm} onClose={() => setConfirm(null)} />}
      {toast && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default Settings;
